import React from 'react'
import { motion } from 'framer-motion'
import { FaGithub, FaLinkedin, FaEnvelope } from 'react-icons/fa'

const HeroSection = () => {
  const profileImage = '/image.gif'

  return (
    <motion.aside
      // Desktop: fixed left sidebar | Mobile: regular section
      className="md:fixed md:left-4 md:top-4 md:bottom-4 md:w-80 w-full relative z-50 flex flex-col"
      initial={{ x: -100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
    >
      {/* Card container */}
      <div className="glass rounded-3xl p-6 md:h-full h-auto flex flex-col shadow-2xl md:overflow-y-auto overflow-visible">
        
        {/* Profile Image */}
        <motion.div
          className="relative mb-6"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="relative">
            <div className="absolute -inset-2 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-2xl opacity-20 blur-xl" />
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src={profileImage}
                alt="Anisur Rahaman Sakib"
                className="w-full md:max-w-full max-w-sm mx-auto object-cover"
              />
              
              {/* Status badge */}
              <div className="absolute bottom-2 left-2 bg-green-500/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-2 shadow-lg">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span className="text-white text-xs font-medium">Available</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Greeting */}
        <motion.div
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <span className="text-blue-400 text-sm font-medium">👋 Hello, I'm</span>
        </motion.div>

        {/* Name */}
        <motion.h1
          className="text-3xl md:text-3xl font-bold mb-2 leading-tight bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent"
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            Anisur Rahaman
          </span>
          <br />
          <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent ">Sakib</span>
        </motion.h1>

        {/* Title */}
        <motion.h2
          className="text-lg md:text-lg text-gray-300 font-medium mb-4"
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          Senior Software Engineer
        </motion.h2>

        {/* Bio */}
        <motion.p
          className="text-sm md:text-sm text-gray-400 mb-4 leading-relaxed"
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          Building scalable web applications and leading development teams for over a decade.
          Expert in PHP, JavaScript, DevOps, and creating seamless user experiences.
        </motion.p>

        {/* Skills tags */}
        <motion.div
          className="flex flex-wrap gap-2 mb-6"
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          {['Full-Stack', 'DevOps', 'Agile', 'System Design'].map((skill) => (
            <span
              key={skill}
              className="px-3 py-1 glass rounded-full text-xs text-gray-300 hover:bg-white/20 transition-colors duration-300 cursor-default"
            >
              {skill}
            </span>
          ))}
        </motion.div>

        {/* Social links */}
        <motion.div
          className="flex gap-3 mb-auto"
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <motion.a
            href="mailto:anisursakib@gmail.com"
            className="w-10 h-10 glass rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-300"
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Email"
          >
            <FaEnvelope className="text-gray-300 hover:text-white transition-colors" />
          </motion.a>
          <motion.a
            href="https://github.com"
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 glass rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-300"
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.9 }}
            aria-label="GitHub"
          >
            <FaGithub className="text-gray-300 hover:text-white transition-colors" />
          </motion.a>
          <motion.a
            href="https://linkedin.com"
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 glass rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-300"
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.9 }}
            aria-label="LinkedIn"
          >
            <FaLinkedin className="text-gray-300 hover:text-white transition-colors" />
          </motion.a>
        </motion.div>

        {/* Bottom Links */}
        <motion.div
          className="pt-4 border-t border-white/10 space-y-3"
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.9 }}
        >
          <motion.a
            href="#contact"
            className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center gap-2 text-white font-medium hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Get In Touch
          </motion.a>

          <motion.a
            href="#projects"
            className="w-full px-6 py-3 glass rounded-xl flex items-center justify-center gap-2 text-white font-medium hover:bg-white/20 transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            View Projects
          </motion.a>
        </motion.div>
      </div>
    </motion.aside>
  )
}

export default HeroSection